import type { PluginListener } from '@tauri-apps/api/core'
import type { PushNotification } from '@spicavi/tauri-plugin-push-notifications'

import { env } from '@/lib/config/env'

import { notificationsApi } from '../api'

const NATIVE_PUSH_PREF_KEY = 'kuniri.push.native.enabled.v1'

export type NativePushErrorCode = 'unsupported' | 'denied' | 'error'

export class NativePushError extends Error {
  readonly code: NativePushErrorCode

  constructor(code: NativePushErrorCode) {
    super(code)
    this.name = 'NativePushError'
    this.code = code
  }
}

function nativePushEnabledByUser(): boolean {
  try {
    return localStorage.getItem(NATIVE_PUSH_PREF_KEY) === 'true'
  } catch {
    return false
  }
}

function setNativePushEnabledByUser(enabled: boolean): void {
  try {
    localStorage.setItem(NATIVE_PUSH_PREF_KEY, String(enabled))
  } catch {
    // The server row is still authoritative for delivery.
  }
}

export function nativePushSupported(): boolean {
  return (
    env.isTauri() &&
    env.clientPlatform === 'android' &&
    env.providers.nativePush
  )
}

async function plugin() {
  return import('@spicavi/tauri-plugin-push-notifications')
}

export async function nativePushPermissionGranted(): Promise<boolean> {
  if (!nativePushSupported()) return false
  try {
    return await (await plugin()).isPermissionGranted()
  } catch {
    return false
  }
}

async function registerToken(token: string): Promise<void> {
  await notificationsApi.registerPushDevice({
    platform: 'android',
    token,
    environment: 'production',
    appVersion: env.appVersion,
  })
}

/** Requests the Android 13+ permission and registers the FCM token. */
export function nativePushEnabledLocally(): boolean {
  return nativePushEnabledByUser()
}

export async function enableNativePush(): Promise<void> {
  if (!nativePushSupported()) throw new NativePushError('unsupported')
  try {
    const fcm = await plugin()
    const granted = await fcm.requestPermission()
    if (!granted) throw new NativePushError('denied')
    const token = await fcm.registerForPush()
    await registerToken(token)
    setNativePushEnabledByUser(true)
  } catch (error) {
    if (error instanceof NativePushError) throw error
    throw new NativePushError('error')
  }
}

/**
 * Re-registers the current FCM token on every authenticated launch. Tokens can
 * rotate, and the backend upserts by `(platform, token)`. Never prompts.
 */
export async function syncNativePush(): Promise<void> {
  if (!nativePushSupported()) return
  try {
    if (!nativePushEnabledByUser()) return
    const fcm = await plugin()
    if (!(await fcm.isPermissionGranted())) return
    const token = await fcm.registerForPush()
    await registerToken(token)
  } catch {
    // Startup sync is best-effort; the user can retry from the settings card.
  }
}

/** Removes this device's server row. The token stays in the Firebase SDK. */
export async function disableNativePush(): Promise<void> {
  if (!nativePushSupported()) return
  try {
    const fcm = await plugin()
    const token = await fcm.registerForPush()
    await notificationsApi.unregisterPushDevice({
      platform: 'android',
      token,
    })
    setNativePushEnabledByUser(false)
  } catch {
    // Logout must not be blocked by a network/provider failure.
  }
}

export async function listenNativePushReceived(
  handler: (notification: PushNotification) => void,
): Promise<PluginListener | null> {
  if (!nativePushSupported()) return null
  try {
    return await (await plugin()).onNotificationReceived(handler)
  } catch {
    return null
  }
}

export async function listenNativePushTapped(
  handler: (notification: PushNotification) => void,
): Promise<PluginListener | null> {
  if (!nativePushSupported()) return null
  try {
    return await (await plugin()).onNotificationTapped(handler)
  } catch {
    return null
  }
}
